import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Erreur stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-white mb-6">
          📊 Tableau de bord
        </h3>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {Object.entries(getStatsConfig(stats)).map(([key, config]) => (
            <div key={key} className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 backdrop-blur-sm border border-slate-600/50 rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="text-3xl">{config.icon}</div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-white">{config.value}</p>
                  <p className="text-sm text-slate-400">{config.label}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className={`px-2 py-1 rounded-full text-xs ${config.trend === 'up' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                  {config.trend === 'up' ? '↗' : '→'} {config.change}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <QuickActions />
        
        {/* Recent Activity */}
        <RecentActivity />
      </div>
    </div>
  );
};

const getStatsConfig = (stats) => {
  // Configuration différente selon le type d'utilisateur
  if (stats.missions_actives !== undefined) {
    // Magasin
    return {
      missions: {
        icon: '🎯',
        value: stats.missions_actives || 0,
        label: 'Missions actives',
        trend: 'up',
        change: '+12%'
      },
      candidatures: {
        icon: '📋',
        value: stats.candidatures_recues || 0,
        label: 'Candidatures reçues',
        trend: 'up',
        change: '+8%'
      },
      contrats: {
        icon: '📄',
        value: stats.contrats_en_cours || 0,
        label: 'Contrats en cours',
        trend: 'neutral',
        change: 'Stable'
      },
      success: {
        icon: '✅',
        value: '94%',
        label: 'Taux de réussite',
        trend: 'up',
        change: '+2%'
      }
    };
  } else {
    // Opticien
    return {
      candidatures: {
        icon: '📤',
        value: stats.candidatures_envoyees || 0,
        label: 'Candidatures envoyées',
        trend: 'up',
        change: '+5'
      },
      contrats: {
        icon: '📄',
        value: stats.contrats_en_cours || 0,
        label: 'Contrats en cours',
        trend: 'neutral',
        change: 'Actif'
      },
      heures: {
        icon: '⏰',
        value: stats.heures_effectuees || 0,
        label: 'Heures effectuées',
        trend: 'up',
        change: '+24h'
      },
      retrocessions: {
        icon: '💰',
        value: `${stats.retrocessions || 0}€`,
        label: 'Rétrocessions',
        trend: 'up',
        change: '+15%'
      }
    };
  }
};

const QuickActions = () => {
  return (
    <div className="bg-gradient-to-r from-blue-600/10 to-cyan-600/10 border border-blue-500/30 rounded-xl p-6 mb-8">
      <h4 className="text-lg font-semibold text-white mb-4">🚀 Actions rapides</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button className="bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/50 rounded-lg p-4 transition-all duration-200 text-left">
          <div className="text-2xl mb-2">➕</div>
          <h5 className="font-medium">Nouvelle mission</h5>
          <p className="text-sm text-blue-400">Publier une offre</p>
        </button>
        
        <button className="bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/50 rounded-lg p-4 transition-all duration-200 text-left">
          <div className="text-2xl mb-2">👥</div>
          <h5 className="font-medium">Candidatures</h5>
          <p className="text-sm text-emerald-400">Voir les profils</p>
        </button>
        
        <button className="bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/50 rounded-lg p-4 transition-all duration-200 text-left">
          <div className="text-2xl mb-2">💬</div>
          <h5 className="font-medium">Messages</h5>
          <p className="text-sm text-purple-400">3 non lus</p>
        </button>
      </div>
    </div>
  );
};

const RecentActivity = () => {
  const [activities, setActivities] = useState([
    {
      id: 1,
      type: 'candidature',
      title: 'Nouvelle candidature reçue',
      description: 'Marie Dupont a postulé pour "Opticien H/F - Paris 15ème"',
      time: 'Il y a 2h',
      icon: '👤'
    },
    {
      id: 2,
      type: 'mission',
      title: 'Mission publiée avec succès',
      description: 'Votre mission "Remplaçant optométriste" est maintenant visible',
      time: 'Il y a 5h',
      icon: '🎯'
    },
    {
      id: 3,
      type: 'contrat',
      title: 'Contrat signé',
      description: 'Le contrat avec Jean Martin a été signé électroniquement',
      time: 'Hier',
      icon: '✅'
    }
  ]);

  return (
    <div className="bg-gradient-to-br from-slate-800/30 to-slate-700/30 border border-slate-600/50 rounded-xl p-6">
      <h4 className="text-lg font-semibold text-white mb-6">📈 Activité récente</h4>
      
      <div className="space-y-4">
        {activities.map((activity) => (
          <div key={activity.id} className="flex items-start space-x-4 p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
            <div className="text-2xl">{activity.icon}</div>
            <div className="flex-1">
              <h5 className="font-medium text-white">{activity.title}</h5>
              <p className="text-sm text-slate-400 mt-1">{activity.description}</p>
              <p className="text-xs text-slate-500 mt-2">{activity.time}</p>
            </div>
          </div>
        ))}
      </div>
      
      <button className="w-full mt-4 text-blue-400 hover:text-blue-300 text-center py-2 transition-colors">
        Voir toute l'activité →
      </button>
    </div>
  );
};